import { type QueryClient, useQueryClient } from '@tanstack/react-query';

import { COMMUNITY_DIRECTORY_CACHE_PREFIX } from '~/constants/community';

import { useCommunityActionBase } from './useCommunityActionBase';

/** First segment of every query key invalidated after a feed or follow action. */
export const COMMUNITY_FEED_CACHE_PREFIXES = [
  'community-posts', // "For you" feed + every channel
  'community-member-posts',
  'community-member-comments',
  'community-comments', // a single post's comments
  'community-search', // post results on the /search route
  COMMUNITY_DIRECTORY_CACHE_PREFIX,
] as const;

/**
 * Invalidates every feed/comment cache after an action so the change is pulled fresh from the
 * server (default `refetchType: 'active'` → refetches the mounted feed, marks others stale).
 * A single shared mutation invalidates once — no per-card fan-out.
 */
export const invalidateCommunityFeedQueries = (queryClient: QueryClient): void => {
  for (const prefix of COMMUNITY_FEED_CACHE_PREFIXES) {
    queryClient.invalidateQueries({ queryKey: [prefix] });
  }
};

interface CommunityFeedActionConfig<Vars> {
  buildRequest: (vars: Vars) => { path: string; fields: Record<string, string> };
  onSuccess?: (data: unknown, vars: Vars) => void;
}

/**
 * Feed/comment action mutation: POSTs the action via `useCommunityActionBase` (which toasts the
 * result) and invalidates every feed/comment cache on settle so the change reflects from the
 * server.
 */
export const useCommunityFeedActionMutation = <Vars>({ buildRequest, onSuccess }: CommunityFeedActionConfig<Vars>) => {
  const queryClient = useQueryClient();

  return useCommunityActionBase<Vars>({
    buildRequest,
    onSuccess,
    onSettled: () => invalidateCommunityFeedQueries(queryClient),
  });
};
